import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { observer } from "mobx-react-lite"
import { DeviceEventEmitter } from "~/ui/primitives"
import { useStores } from "~models"
import { globalToast } from "~utils"

export type NoticeStatus = "unread" | "readed"

export type NoticeItem = {
  extra?: any
  id: string | number
  type?: number
  label: string
  content?: string
  created?: string
  status: NoticeStatus
  is_read?: boolean
}

export type NoticeSection = {
  type: "order" | "transfer" | "system"
  label: string
  icon: string
  data: NoticeItem[]
}

type NoticeCenterContextValue = {
  sections: NoticeSection[]
  refreshing: boolean
  loadingMore: boolean
  iconMap: Record<NoticeSection["type"], string>
  onRefresh: () => void
  onEndReached: () => void
  getNoticeById: (id: string | null) => NoticeItem | undefined
  markNoticeReadLocally: (id: string | number) => void
  refreshList: () => void
  replaceList: (list: any[]) => void
}

const NoticeCenterContext = createContext<NoticeCenterContextValue | null>(null)

export function useNoticeCenter() {
  const ctx = useContext(NoticeCenterContext)
  if (!ctx) throw new Error("useNoticeCenter must be used within NoticeCenterProvider")
  return ctx
}

const PAGE_SIZE = 20

const iconMap: Record<NoticeSection["type"], string> = {
  order: "jytz",
  transfer: "hb",
  system: "xttz",
}

function normalizeItem(item: any): NoticeItem {
  const id = item?.id ?? null
  const label = item?.title ?? "通知"
  const content = item?.content ?? ""
  const created = item?.created ?? ""
  const is_read = item?.is_read ?? false
  const status: NoticeStatus = is_read ? "readed" : "unread"
  return {
    id,
    type: item?.type,
    label,
    content,
    created,
    status,
    is_read,
    extra: item?.extra,
  }
}

export const NoticeCenterProvider = observer(function NoticeCenterProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { apiStore } = useStores()
  const [rawList, setRawList] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const fetchPage = useCallback(
    async ({ nextPage, replace }: { nextPage: number; replace: boolean }) => {
      const setBusy = replace ? setRefreshing : setLoadingMore
      setBusy(true)
      try {
        const res = await apiStore.getUserNotificationList({
          params: { page: nextPage, limit: PAGE_SIZE },
        })
        const result = Array.isArray(res?.data) ? res.data : []
        setHasMore(result.length >= PAGE_SIZE)
        setPage(nextPage)
        setRawList((prev) => {
          const base = replace ? [] : prev
          const merged = [...base, ...result]
          const seen = new Set<string | number>()
          return merged.filter((x) => {
            const key =
              x?.id ??
              x?._id ??
              `${x?.type ?? "unknown"}-${x?.created_at ?? x?.created ?? ""}-${
                x?.title ?? x?.label ?? ""
              }`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
        })
      } catch (err: any) {
        setHasMore(false)
        globalToast.show(err?.message || "获取通知列表失败")
      } finally {
        setBusy(false)
      }
    },
    [apiStore],
  )

  useEffect(() => {
    fetchPage({ nextPage: 1, replace: true })
  }, [fetchPage])

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      "RefreshNoticeCenterList",
      (list?: unknown) => {
        if (Array.isArray(list)) {
          setHasMore(list.length >= PAGE_SIZE)
          setPage(1)
          setRawList(list)
          return
        }
        setHasMore(true)
        fetchPage({ nextPage: 1, replace: true })
      },
    )
    return () => sub.remove()
  }, [fetchPage])

  const sections: NoticeSection[] = useMemo(() => {
    const normalized = rawList.map(normalizeItem)
    const order = normalized.filter((x) => [1, 3].includes(Number(x.type)))
    const transfer = normalized.filter((x) => [2, 4].includes(Number(x.type)))
    const system = normalized.filter((x) => [5].includes(Number(x.type)))
    return [
      { type: "order", label: "交易订单通知", icon: iconMap.order, data: order },
      { type: "transfer", label: "待领取红包及转账通知", icon: iconMap.transfer, data: transfer },
      { type: "system", label: "系统信息通知", icon: iconMap.system, data: system },
    ]
  }, [rawList])

  const onRefresh = useCallback(() => {
    if (refreshing) return
    setHasMore(true)
    fetchPage({ nextPage: 1, replace: true })
  }, [fetchPage, refreshing])

  const onEndReached = useCallback(() => {
    if (refreshing || loadingMore || !hasMore) return
    fetchPage({ nextPage: page + 1, replace: false })
  }, [fetchPage, hasMore, loadingMore, page, refreshing])

  const markNoticeReadLocally = useCallback((id: string | number) => {
    setRawList((prev) =>
      prev.map((item) => {
        const itemId = item?.id ?? item?._id
        if (String(itemId) !== String(id)) return item
        return { ...item, is_read: true }
      }),
    )
  }, [])

  const getNoticeById = useCallback(
    (id: string | null) => {
      if (!id) return undefined
      return sections.flatMap((section) => section.data).find((item) => String(item.id) === id)
    },
    [sections],
  )

  const refreshList = useCallback(() => {
    setHasMore(true)
    fetchPage({ nextPage: 1, replace: true })
  }, [fetchPage])

  const replaceList = useCallback((list: any[]) => {
    setHasMore(list.length >= PAGE_SIZE)
    setPage(1)
    setRawList(list)
  }, [])

  const value = useMemo(
    () => ({
      sections,
      refreshing,
      loadingMore,
      iconMap,
      onRefresh,
      onEndReached,
      getNoticeById,
      markNoticeReadLocally,
      refreshList,
      replaceList,
    }),
    [
      sections,
      refreshing,
      loadingMore,
      onRefresh,
      onEndReached,
      getNoticeById,
      markNoticeReadLocally,
      refreshList,
      replaceList,
    ],
  )

  return <NoticeCenterContext.Provider value={value}>{children}</NoticeCenterContext.Provider>
})

export function getNoticeSectionIcon(type?: number) {
  if ([1, 3].includes(Number(type))) return iconMap.order
  if ([2, 4].includes(Number(type))) return iconMap.transfer
  if ([5].includes(Number(type))) return iconMap.system
  return iconMap.system
}
